import React from "react";
import { HelmetProvider } from "react-helmet-async";
import {
  ApolloProvider,
  ApolloClient,
  InMemoryCache,
  from,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { HttpLink } from "@apollo/client/link/http";
import { FormProvider } from "./context/FormContext";
import ErrorBoundary from "./Components/ErrorBoundary";
import App from "./App";
import ThemeProvider from "./theme/ThemeProvider";

/**
 * Create Apollo Client with error handling
 */
const createApolloClient = () => {
  // Error handling link
  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path }) => {
        console.error(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
        );
      });
    }

    if (networkError) {
      console.error(`[Network error]: ${networkError}`);
    }
  });

  // HTTP link to GraphQL server
  const httpLink = new HttpLink({
    uri:
      process.env.REACT_APP_API_URL ||
      "https://api-reg.eventegration.com/graphql",
    credentials: "same-origin",
  });

  // Create cache with type policies
  const cache = new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          // Define cache key generation for entities if needed
        },
      },
    },
  });

  // Create and configure Apollo Client
  return new ApolloClient({
    link: from([errorLink, httpLink]),
    cache,
    connectToDevTools: process.env.NODE_ENV !== "production",
    defaultOptions: {
      watchQuery: {
        fetchPolicy: "cache-and-network",
        errorPolicy: "all",
      },
      query: {
        fetchPolicy: "network-only",
        errorPolicy: "all",
      },
      mutate: {
        errorPolicy: "all",
      },
    },
  });
};

/**
 * Application wrapper component with all providers
 * Note: BrowserRouter is intentionally NOT included here to avoid nesting routers.
 * The Router component should be included only in App.jsx.
 *
 * @returns {JSX.Element} Wrapped application
 */
const AppWrapper = () => {
  const client = React.useMemo(() => createApolloClient(), []);

  return (
    <ErrorBoundary>
      <ApolloProvider client={client}>
        <HelmetProvider>
          <ThemeProvider>
            <FormProvider>
              <App />
            </FormProvider>
          </ThemeProvider>
        </HelmetProvider>
      </ApolloProvider>
    </ErrorBoundary>
  );
};

export default AppWrapper;
